import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { SpinnerSize, SpinnerVariant } from '../spinner/spinner.component';

/**
 * Loading State Component
 *
 * A wrapper component that displays a loading indicator over or instead of content.
 * Provides consistent loading UX across the application.
 *
 * Usage:
 * ```html
 * <!-- Overlay mode (default): Shows spinner over content -->
 * <app-loading-state [loading]="isLoading">
 *   <div>Your content here</div>
 * </app-loading-state>
 *
 * <!-- Replace mode: Hides content while loading -->
 * <app-loading-state [loading]="isLoading" [overlay]="false">
 *   <div>Your content here</div>
 * </app-loading-state>
 *
 * <!-- With message and custom size -->
 * <app-loading-state [loading]="isLoading" message="Loading data..." size="lg">
 *   <div>Your content here</div>
 * </app-loading-state>
 *
 * <!-- Minimum height for empty containers -->
 * <app-loading-state [loading]="isLoading" minHeight="200px">
 *   <div>Your content here</div>
 * </app-loading-state>
 * ```
 */
@Component({
  selector: 'app-loading-state',
  standalone: false,
  templateUrl: './loading-state.component.html',
  styleUrl: './loading-state.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoadingStateComponent {
  /** Whether content is currently loading */
  @Input() loading = false;

  /** Optional message to display below the spinner */
  @Input() message = '';

  /** Size of the loading spinner */
  @Input() size: SpinnerSize = 'md';

  /** Color variant of the spinner */
  @Input() variant: SpinnerVariant = 'primary';

  /**
   * Display mode:
   * - true (default): Shows spinner overlay on top of content
   * - false: Replaces content with spinner while loading
   */
  @Input() overlay = true;

  /** Minimum height when loading (useful for empty containers) */
  @Input() minHeight = '';
}
